import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { setUserFromToken } from "../feature/userSlice";
import { setAccessToken } from "../utils/authToken";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Business as BusinessIcon,
  CheckCircleOutline as CheckIcon,
} from "@mui/icons-material";

// ─── Module catalogue ─────────────────────────────────────────────────────────

const MODULES = [
  {
    slug:        "weldassure",
    label:       "WeldAssure",
    price:       "$25/mo or $244.99/yr",
    annual:      244.99,
    monthly:     25.00,
    tagline:     "Heat traceability, CMTR management & release packages",
    bullets:     ["Incoming heat log", "CMTR upload & verification", "Engineering release packages"],
    color:       "#1976d2",
  },
  {
    slug:        "sfm_inspection_plus",
    label:       "SFM Inspection Plus",
    price:       "$25/mo or $244.99/yr",
    annual:      244.99,
    monthly:     25.00,
    tagline:     "QA/NCR management and dimensional inspection",
    bullets:     ["Non-conformance reports", "Corrective actions & COPQ", "Audit-ready NCR log"],
    color:       "#9c27b0",
  },
  {
    slug:        "sfm_live_operations",
    label:       "SFM Operations",
    price:       "Free",
    annual:      0,
    monthly:     0,
    tagline:     "Live job tracking, kiosk, and BOL chain of custody",
    bullets:     ["Live job & task board", "Touch-screen kiosk view", "Bill of Lading with load seals", "iOS app"],
    color:       "#2e7d32",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function RegisterTenantPage() {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const [step,           setStep]           = useState(1); // 1 = modules, 2 = details
  const [modules,        setModules]        = useState([]);
  const [agreedToTerms,  setAgreedToTerms]  = useState(false);
  const [form,    setForm]    = useState({
    companyName: "",
    firstName:   "",
    lastName:    "",
    email:       "",
    password:    "",
    confirm:     "",
    phone:       "",
  });
  const [error,   setError]   = useState("");
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(false);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const toggleModule = (slug) => {
    setModules((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const handleField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const selectedModules = () => MODULES.filter((m) => modules.includes(m.slug));
  const totalAnnual  = () => selectedModules().reduce((s, m) => s + m.annual, 0).toFixed(2);
  const totalMonthly = () => selectedModules().reduce((s, m) => s + m.monthly, 0).toFixed(2);

  // ── Step 1 → 2 ───────────────────────────────────────────────────────────

  const goToDetails = () => {
    if (modules.length === 0) {
      setError("Select at least one module to continue.");
      return;
    }
    setError("");
    setStep(2);
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setError("");

    if (!form.companyName.trim()) { setError("Company name is required");      return; }
    if (!form.firstName.trim())   { setError("First name is required");        return; }
    if (!form.lastName.trim())    { setError("Last name is required");         return; }
    if (!form.email.trim())       { setError("Email is required");             return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }

    setSaving(true);
    try {
      const { data } = await axios.post("/api/auth/register-tenant", {
        companyName: form.companyName,
        firstName:   form.firstName,
        lastName:    form.lastName,
        email:       form.email.trim().toLowerCase(),
        password:    form.password,
        phone:       form.phone || undefined,
        modules,
      });

      const token = data.data?.token;
      if (token) {
        setAccessToken(token);
        const decoded = jwt_decode(token);
        dispatch(setUserFromToken({ token, decodedToken: decoded }));
      }

      setDone(true);
      setTimeout(() => navigate("/dashboard/app"), 2500);
    } catch (e) {
      setError(e.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────

  if (done) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f9fafb" }}>
        <Stack alignItems="center" spacing={2} sx={{ maxWidth: 420, textAlign: "center", p: 4 }}>
          <CheckIcon sx={{ fontSize: 64, color: "success.main" }} />
          <Typography variant="h5" fontWeight={700}>You're in — welcome to SFM!</Typography>
          <Typography color="text.secondary">
            Your 14-day trial has started. Redirecting to your dashboard…
          </Typography>
          <CircularProgress size={24} />
        </Stack>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", py: 3, px: 1 }}>

      {/* Header */}
      <Stack alignItems="center" spacing={0.5} mb={3}>
        <Typography variant="h5" fontWeight={800} color="primary.main">Start your free trial</Typography>
        <Typography color="text.secondary" variant="body2">
          14 days free — no credit card required.
        </Typography>
      </Stack>

      {/* Step indicator */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <Chip label="1  Choose Modules" color={step === 1 ? "primary" : "default"} variant={step === 1 ? "filled" : "outlined"} size="small" />
        <Box sx={{ width: 24, height: 1, bgcolor: "divider" }} />
        <Chip label="2  Your Details"   color={step === 2 ? "primary" : "default"} variant={step === 2 ? "filled" : "outlined"} size="small" />
      </Stack>

      {/* ── STEP 1: Module picker ─────────────────────────────────────────── */}
      {step === 1 && (
        <Box sx={{ width: "100%" }}>
          <Stack spacing={1.5} mb={2}>
            {MODULES.map((m) => {
              const selected = modules.includes(m.slug);
              return (
                <Box
                  key={m.slug}
                  onClick={() => toggleModule(m.slug)}
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    p: 2.25,
                    pl: 2.5,
                    bgcolor: "white",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: selected ? m.color : "divider",
                    borderLeft: `5px solid ${m.color}`,
                    boxShadow: selected ? 3 : 0,
                    transition: "all 0.15s",
                    "&:hover": { boxShadow: 3, borderColor: m.color },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={800} fontSize={16} sx={{ color: m.color, lineHeight: 1.2 }}>{m.label}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{m.tagline}</Typography>
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <Chip
                        label={selected ? "✓ Selected" : "Select"}
                        size="small"
                        color={selected ? "success" : "default"}
                        variant={selected ? "filled" : "outlined"}
                      />
                      <Typography fontWeight={800} sx={{ color: m.color, whiteSpace: "nowrap" }}>{m.price}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{m.annual > 0 ? "after trial ends" : "included with every account"}</Typography>
                    </Stack>
                  </Stack>
                  <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                    {m.bullets.map((b) => (
                      <Stack key={b} direction="row" spacing={0.75} alignItems="center">
                        <CheckIcon sx={{ fontSize: 15, color: m.color }} />
                        <Typography variant="caption" color="text.secondary">{b}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          {/* Core platform note */}
          <Box sx={{ bgcolor: "white", borderRadius: 2, p: 2, mb: 2, boxShadow: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Core platform</strong> (included with every account): Jobs, Tasks, Sequences, Inventory,
              Purchase Orders, Contacts, Vendors, Users &amp; Roles — no charge.
            </Typography>
          </Box>

          {modules.length > 0 && (
            <Box sx={{ bgcolor: "primary.50", border: "1px solid", borderColor: "primary.200", borderRadius: 2, p: 2, mb: 2 }}>
              <Typography variant="body2">
                <strong>Selected:</strong>{" "}
                {MODULES.filter((m) => modules.includes(m.slug)).map((m) => m.label).join(", ")}
                {" · "}
                {Number(totalAnnual()) > 0
                  ? <><strong>${totalMonthly()} / mo or ${totalAnnual()} / yr</strong> after your free trial</>
                  : <strong>No charge</strong>}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Software only. Hardware is not included: you supply your own devices, displays, printers, scanners and network.
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Button variant="contained" size="large" fullWidth onClick={goToDetails}>
            Continue →
          </Button>
        </Box>
      )}

      {/* ── STEP 2: Account details ────────────────────────────────────────── */}
      {step === 2 && (
        <Box sx={{ maxWidth: 480, width: "100%", bgcolor: "white", borderRadius: 2, p: 4, boxShadow: 2 }}>
          <Button size="small" onClick={() => setStep(1)} sx={{ mb: 2 }}>← Back</Button>
          <Typography variant="h6" fontWeight={700} mb={2}>Set up your account</Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Stack spacing={2}>
            <TextField
              label="Company / Shop Name"
              value={form.companyName}
              onChange={handleField("companyName")}
              required fullWidth size="small"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="First Name" value={form.firstName} onChange={handleField("firstName")} required fullWidth size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Last Name"  value={form.lastName}  onChange={handleField("lastName")}  required fullWidth size="small" />
              </Grid>
            </Grid>
            <TextField
              label="Work Email"
              type="email"
              value={form.email}
              onChange={handleField("email")}
              required fullWidth size="small"
            />
            <TextField
              label="Phone (optional)"
              value={form.phone}
              onChange={handleField("phone")}
              fullWidth size="small"
            />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={handleField("password")}
              required fullWidth size="small"
              helperText="Minimum 8 characters"
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={form.confirm}
              onChange={handleField("confirm")}
              required fullWidth size="small"
            />

            <Box sx={{ bgcolor: "grey.50", borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>14-day free trial</strong> — no credit card required. You'll be billed only after your trial ends
                if you choose to continue. You can cancel anytime.
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  I agree to the{" "}
                  <Link component={RouterLink} to="/terms" target="_blank" rel="noopener">
                    Terms of Service
                  </Link>
                  {" "}and{" "}
                  <Link component={RouterLink} to="/privacy" target="_blank" rel="noopener">
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={saving || !agreedToTerms}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? "Creating your account…" : "Start Free Trial"}
            </Button>
          </Stack>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" mt={4}>
        Already have an account?{" "}
        <Link component={RouterLink} to="/login">Log in</Link>
      </Typography>
    </Box>
  );
}
